import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import I18n from 'i18n-js';
import { Button } from '@duxton-web/ui';
import ModalDrawer from 'components/drawer/modal-drawer';
import IconMapEdit from 'components/icons/icon-map-edit';
import scribeTrack from 'scribe/scribe-track';
import { ADD_INSTRUCTION_POSITION } from 'scribe/constant';
import { inGrabApp } from 'common/utils/platform';

import instrumentationDuck from 'ducks/instrumentation-duck';

import AddInstructionButton from '../button/add-instruction-button';
import arrowSvg from '../../public/images/arrow.svg';
import documentCheckedSvg from '../../public/images/document-checked.svg';
import styles from './location-action.less';

const ActionButton = ({ onClick, icon, title }) => (
  <Button
    className={styles.actionButton}
    onClick={onClick}
    aria-label="action-button"
  >
    <div className={styles.action}>
      <img src={icon} alt="action" />
      <p>{title}</p>
    </div>

    <div className={styles.arrowContainer}>
      <img src={arrowSvg} alt="arrow" />
    </div>
  </Button>
);

ActionButton.propTypes = {
  onClick: PropTypes.func,
  icon: PropTypes.string,
  title: PropTypes.string,
};

ActionButton.defaultProps = {
  onClick: () => {},
  icon: '',
  title: '',
};

export const ViewProofButton = ({ image, type }) => {
  const { orderId, trackingPageType, trackingSource } = useSelector(
    instrumentationDuck.selectors.instrumentation,
  );

  const isPickup = type.toUpperCase() === 'PICKUP';

  const [isOpened, setIsOpened] = useState(false);

  const sendInstrumentation = useCallback(
    () => {
      scribeTrack.expressTracking.viewProof({
        trackingSource,
        orderIdDisplayed: orderId,
        isInApp: inGrabApp(),
        trackingPageType,
      });
    },
    [orderId, trackingPageType, trackingSource],
  );

  const openDrawer = useCallback(
    () => {
      sendInstrumentation();
      setIsOpened(true);
    },
    [sendInstrumentation],
  );
  const closeDrawer = useCallback(() => setIsOpened(false), []);

  return (
    <div>
      <ActionButton
        title={
          isPickup
            ? I18n.t('deliveryDetails.proof.popTitle')
            : I18n.t('deliveryDetails.proof.podTitle')
        }
        icon={documentCheckedSvg}
        onClick={openDrawer}
      />
      <ModalDrawer
        open={isOpened}
        title={
          isPickup
            ? I18n.t('deliveryDetails.proof.popTitle')
            : I18n.t('deliveryDetails.proof.podTitle')
        }
        buttonText={I18n.t('deliveryDetails.proof.back')}
        content={
          <div className={styles.proofContent}>
            <div className={styles.imageContainer}>
              <img className={styles.image} src={image} alt="pop" />
            </div>
          </div>
        }
        onSubmit={closeDrawer}
        onClose={closeDrawer}
      />
    </div>
  );
};

ViewProofButton.propTypes = {
  image: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['pickup', 'dropoff']),
};

ViewProofButton.defaultProps = {
  type: 'dropoff',
};

export const AddInstructionActionButton = ({ name, address, guid }) => {
  const { orderId, trackingPageType, trackingSource } = useSelector(
    instrumentationDuck.selectors.instrumentation,
  );

  const sendInstrumentation = useCallback(
    () => {
      scribeTrack.expressTracking.addInstruction({
        trackingSource,
        addInstructionPosition: ADD_INSTRUCTION_POSITION.POI_SIDE_BAR,
        orderIdDisplayed: orderId,
        isInApp: inGrabApp(),
        trackingPageType,
      });
    },
    [orderId, trackingPageType, trackingSource],
  );

  return (
    <AddInstructionButton
      entry={ADD_INSTRUCTION_POSITION.POI_SIDE_BAR}
      onClick={sendInstrumentation}
      buttonClassName={styles.actionButton}
      title={name}
      address={address}
      guid={guid}
    >
      <div className={styles.action}>
        <IconMapEdit className={styles.mapEditIcon} />
        <p>{I18n.t('deliveryDetails.instructions.addInstructions')}</p>
      </div>

      <div className={styles.arrowContainer}>
        <img src={arrowSvg} alt="arrow" />
      </div>
    </AddInstructionButton>
  );
};

AddInstructionActionButton.propTypes = {
  name: PropTypes.string,
  address: PropTypes.string,
  guid: PropTypes.string.isRequired,
};

AddInstructionActionButton.defaultProps = {
  name: '',
  address: '',
};

export const InstructionNote = ({ text }) => (
  <div className={styles.instructionContainer}>
    <IconMapEdit className={styles.mapEditIcon} />

    <p>{text}</p>
  </div>
);

InstructionNote.propTypes = {
  text: PropTypes.string,
};

InstructionNote.defaultProps = {
  text: '',
};
